/**
 * web-studio-sites D4 — the kit's one page runtime. One React root holds every
 * shared provider once (the query client, the session, the messages panel and
 * the live chat, the toasts, and a router on the document's own address) and
 * draws each placeholder's piece through a portal into a frame of its own, the
 * placeholder's only child. So one panel, one live connection and one sign-out
 * reach every piece on the page. The frame is a size container (base.css), so a
 * placed piece takes its narrow layout from the width its placeholder gives it.
 *
 * A placeholder is filled only when its piece is placeable and its plug-in is
 * switched on for the studio (the studio read's `plugins`); otherwise it stays
 * empty with one console warning naming the piece and the plug-in. Placeholders
 * added later are found by a `MutationObserver`.
 *
 * D7: a piece with a site drawing (`Telify.draw`) runs its behavior half as
 * usual and patches its frame with the drawing's markup on every state
 * change; its `data-action` elements call the piece's checked actions. A drawing
 * that leaves out a part the state requires gives way to the standard look.
 */
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { PLUGINS, type PluginDeclaration } from '@telify/shared/browser';
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { ToastProvider } from '@/components/Toast/ToastContext';

import { bindSessionEnd, useWebSession } from '../api/session';
import { ShopPage, ShopPageProvider } from '../hooks/useShopPage';
import { useWebDashboard } from '../hooks/useWebAccount';
import { useSiteEvents, useSiteServices, useSiteStudio } from '../api/site';
import { PlacedProvider } from '../contract/runtime';
import { MessagesPanelProvider } from '../messages/panelContext';
import { WebChatProvider } from '../messages/WebChat';
import { argsFrom, checkedActions } from './args';
import { missingPart, missingPartMessage } from './check';
import { Drawings, type Drawing } from './draw';
import { morph } from './morph';
import { dispatchPageEvent } from './pageEvents';
import { KIT_PIECES, type AnyRuntime, type KitPiece } from './pieces';
import { findPlaceholders, KIT_HOST_ATTRIBUTE, placeholderOptions } from './placeholders';

export interface Kit {
  /** Unmounts every placed piece and stops watching the page. */
  readonly stop: () => void;
}

export interface KitOptions extends KitPlacementsProps {
  /** The page's query client; a new one by default. */
  readonly queryClient?: QueryClient;
}

/** The page's placeholders, kept current as the page changes. */
class Placeholders {
  private list: readonly HTMLElement[] = [];
  private readonly listeners = new Set<() => void>();

  constructor(private readonly scope: HTMLElement) {
    this.list = findPlaceholders(scope);
  }

  readonly scan = (): void => {
    const next = findPlaceholders(this.scope);
    if (next.length === this.list.length && next.every((el, i) => el === this.list[i])) return;
    this.list = next;
    for (const listener of this.listeners) listener();
  };

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly get = (): readonly HTMLElement[] => this.list;
}

/** Each placeholder warns once, however often the page redraws. */
const warned = new WeakSet<HTMLElement>();
function warnOnce(el: HTMLElement, message: string): void {
  if (warned.has(el)) return;
  warned.add(el);
  console.warn(`[telify] ${message}; its placeholder stays empty.`);
}

/** The piece a placeholder may show, or null with the reason warned. */
function placeable(el: HTMLElement, plugins: readonly PluginDeclaration[], enabled: readonly string[]): KitPiece | null {
  const name = el.dataset.telify ?? '';
  const plugin = plugins.find((p) => p.pieces.includes(name));
  if (!plugin) {
    warnOnce(el, `"${name}" is not a piece`);
    return null;
  }
  if (!enabled.includes(plugin.id)) {
    warnOnce(el, `${name}: the ${plugin.id} plug-in is not switched on for this studio`);
    return null;
  }
  const piece = KIT_PIECES.get(name);
  if (!piece?.contract.placeable) {
    warnOnce(el, `${name}: this piece cannot be placed on a page`);
    return null;
  }
  return piece;
}

const FIELDS = 'input, select, textarea';

interface DrawnPieceProps {
  /** The placement's frame, which the drawing's markup is morphed into. */
  readonly el: HTMLElement;
  readonly piece: KitPiece;
  /** The placement's behavior half, the one the standard look draws too. */
  readonly runtime: AnyRuntime;
  readonly drawing: Drawing;
  /** The drawing failed or left out a required part: the standard look takes over. */
  readonly onFail: () => void;
}

/** A placed piece drawn by the site: the site's markup over the placement's behavior half, and the required-part check. */
function DrawnPiece({ el, piece, runtime, drawing, onFail }: DrawnPieceProps) {
  const name = piece.contract.name;
  const state = useRef(runtime.state);
  state.current = runtime.state;
  const actions = useMemo(
    () => checkedActions(piece.contract, runtime.actions as Readonly<Record<string, unknown>>, () => state.current),
    [piece, runtime.actions],
  );
  const current = useRef(actions);
  current.current = actions;

  // Clicks, and changes of a field, on an element with `data-action` call that action with its decoded arguments.
  useEffect(() => {
    const run = (target: HTMLElement) => {
      const action = target.dataset.action ?? '';
      const spec = piece.contract.actions[action];
      const call = current.current[action];
      if (!spec || !call) {
        console.error(`[telify] ${name}: there is no action "${action}"; nothing done.`);
        return;
      }
      call(...argsFrom(target, spec));
    };
    const caller = (e: Event) => {
      const target = e.target instanceof Element ? e.target.closest<HTMLElement>('[data-action]') : null;
      return target && el.contains(target) ? target : null;
    };
    const onClick = (e: MouseEvent) => {
      const target = caller(e);
      if (!target || target.matches(FIELDS)) return;
      e.preventDefault();
      run(target);
    };
    const onChange = (e: Event) => {
      const target = caller(e);
      if (target?.matches(FIELDS)) run(target);
    };
    el.addEventListener('click', onClick);
    el.addEventListener('change', onChange);
    return () => {
      el.removeEventListener('click', onClick);
      el.removeEventListener('change', onChange);
    };
  }, [el, piece, name]);

  useLayoutEffect(() => {
    const giveWay = (message: string) => {
      console.error(message);
      el.replaceChildren();
      onFail();
    };
    let markup: unknown;
    try {
      markup = drawing(runtime.state, actions);
    } catch (err) {
      giveWay(`[telify] ${name}: the drawing failed (${String(err)}); showing the standard drawing.`);
      return;
    }
    if (typeof markup !== 'string') {
      giveWay(`[telify] ${name}: the drawing must return markup text; showing the standard drawing.`);
      return;
    }
    morph(el, markup);
    const missing = missingPart(el, piece.requiredParts(runtime.state));
    if (missing) giveWay(missingPartMessage(name, missing));
  }, [el, piece, name, drawing, runtime.state, actions, onFail]);

  // Cleared while React removes this piece, before the standard look's portal draws into the same frame.
  useLayoutEffect(() => () => el.replaceChildren(), [el]);
  return null;
}

/**
 * The frame a placement draws in (D4): the placeholder's only child, a `telify-piece` size container, or for a
 * piece made for a header row (`fit: 'content'`) marked `content`, which takes its content's width instead.
 */
const FRAME_ATTRIBUTE = 'data-telify-frame';

interface PlacementProps {
  readonly el: HTMLElement;
  readonly piece: KitPiece;
  readonly drawings: Drawings;
  /** Loading out of sight until the page's first look is ready (Placements). */
  readonly held: boolean;
}

function Placement({ el, piece, drawings, held }: PlacementProps) {
  // The options are read once, when the placeholder is first filled.
  const options = useMemo(() => placeholderOptions(el, piece.contract), [el, piece]);
  // One frame for the placement's life: the standard look and a site drawing both draw in it.
  const [frame] = useState(() => document.createElement('div'));
  useLayoutEffect(() => {
    frame.setAttribute(FRAME_ATTRIBUTE, piece.fit === 'content' ? 'content' : '');
    // The stub's copy of a header-row piece stays beside the frame until the frame draws (below).
    const copy = el.querySelector(`:scope > [${REMEMBERED_ATTRIBUTE}]`);
    el.replaceChildren(...(copy ? [copy] : []), frame);
    return () => frame.remove();
  }, [el, frame, piece]);
  useLayoutEffect(() => {
    frame.style.display = held ? 'none' : '';
    const copy = held ? null : el.querySelector(`:scope > [${REMEMBERED_ATTRIBUTE}]`);
    if (!copy) return;
    // Shown, the frame takes the copy's place as soon as it holds its drawing, before the next paint; one that draws
    // nothing (a bag with nothing to sell) lets it go after two seconds.
    let observer: MutationObserver | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const drop = () => {
      copy.remove();
      observer?.disconnect();
      clearTimeout(timer);
    };
    if (frame.childElementCount > 0) return drop();
    observer = new MutationObserver(() => {
      if (frame.childElementCount > 0) drop();
    });
    observer.observe(frame, { childList: true });
    timer = setTimeout(drop, 2000);
    return drop;
  }, [el, frame, held]);
  // Card 4 walk (Gabriel 2026-11-01): a header-row piece is remembered for this tab as drawn, so the next page's stub
  // shows it at once (stub.ts). An open menu is left out of the copy.
  useEffect(() => {
    if (piece.fit !== 'content') return;
    const key = REMEMBERED_KEY + piece.contract.name;
    const save = () => {
      if (frame.style.display === 'none') return;
      try {
        const copy = frame.cloneNode(true) as HTMLElement;
        copy.querySelectorAll('[role="menu"], [data-part="menu"]').forEach((n) => n.remove());
        copy.querySelectorAll('[aria-expanded="true"]').forEach((n) => n.setAttribute('aria-expanded', 'false'));
        if (copy.childElementCount > 0) sessionStorage.setItem(key, copy.innerHTML);
        else sessionStorage.removeItem(key);
      } catch {
        // Storage refused (private mode, full): the next page draws the piece as it loads.
      }
    };
    const observer = new MutationObserver(save);
    observer.observe(frame, { childList: true, subtree: true, attributes: true, characterData: true });
    save();
    return () => observer.disconnect();
  }, [frame, piece]);
  // One behavior half for the placement's life, whichever look draws it, so what the visitor picked stays when
  // her drawing arrives or gives way to the standard look.
  const runtime = piece.use({ options });
  const drawing = useSyncExternalStore(drawings.subscribe, () => drawings.get(piece.contract.name));
  // A drawing that gave way once stays replaced by the standard look on this page.
  const [failed, setFailed] = useState(false);
  const fail = useMemo(() => () => setFailed(true), []);
  if (drawing && !failed) return <DrawnPiece el={frame} piece={piece} runtime={runtime} drawing={drawing} onFail={fail} />;
  return createPortal(<piece.View {...runtime} />, frame);
}

interface PlacementsProps {
  readonly placeholders: Placeholders;
  readonly plugins: readonly PluginDeclaration[];
  readonly drawings: Drawings;
}

/** Pieces drawn from the signed-in client's dashboard read, and the lists that read it for their marks (the mine option). */
const ACCOUNT_PIECES = new Set(['waiting-on-you', 'next-session', 'coming-up', 'home-welcome', 'bookings-list']);
const MINE_PIECES = new Set(['services', 'events-row', 'events-grid']);
/** The pieces that list the studio's events. */
const LIST_OF_EVENTS = new Set(['events-row', 'events-grid']);
const readsAccount = (el: HTMLElement) =>
  ACCOUNT_PIECES.has(el.dataset.telify ?? '') || (MINE_PIECES.has(el.dataset.telify ?? '') && el.dataset.mine === 'true');

/** A header-row piece's copy as last drawn in this tab, and the mark on the stub's copy of it (stub.ts keeps the same two). */
const REMEMBERED_KEY = 'telify:remembered:';
const REMEMBERED_ATTRIBUTE = 'data-telify-remembered';

/** On the page's root once its first look shows (docs/web-kit/v1.md, Placeholders). */
const SHOWN_ATTRIBUTE = 'data-telify-shown';

function Placements({ placeholders, plugins, drawings }: PlacementsProps) {
  const list = useSyncExternalStore(placeholders.subscribe, placeholders.get);
  const studio = useSiteStudio();
  // The page's first look shows at once, never in steps: its pieces load out of sight until who is signed in is known
  // and, for a signed-in client, the dashboard read when a piece here draws from it, so Waiting on you and the lists'
  // marks never land after the rest and push the page down (card 4 walk, Gabriel 2026-11-01). Later changes, a sign-in
  // included, show as they come.
  const session = useWebSession().status;
  const needsAccount = session === 'signedIn' && list.some(readsAccount);
  const dashboard = useWebDashboard({ enabled: needsAccount });
  // The lists' reads start with the studio read, not after it, once the session says which catalog to read: the
  // pieces then find them read or reading (card 4 walk, Gabriel 2026-11-01: one round of reads, not two).
  const catalog = session !== 'loading';
  useSiteServices({ enabled: catalog && list.some((el) => el.dataset.telify === 'services') });
  useSiteEvents({ enabled: catalog && list.some((el) => LIST_OF_EVENTS.has(el.dataset.telify ?? '')) });
  const drawn = useRef(false);
  // A failed read draws the page without waiting for its retries.
  if (session !== 'loading' && (!needsAccount || !dashboard.isPending || dashboard.failureCount > 0)) drawn.current = true;
  // The page is marked once its first look shows, so a site can hold its own content around the pieces until then.
  const shown = Boolean(studio.data) && drawn.current;
  useLayoutEffect(() => {
    if (shown) document.documentElement.setAttribute(SHOWN_ATTRIBUTE, '');
  }, [shown]);
  // Nothing mounts until the studio read says which plug-ins it has, and who is signed in: a piece mounted earlier would
  // read the guest's catalog and then the client's again.
  if (!studio.data || session === 'loading') return null;
  const enabled = studio.data.plugins ?? [];
  // Placed (D4): the pieces' hooks and looks read it. A placeholder that names another piece is a new placement.
  return (
    <PlacedProvider value={true}>
      {list.map((el) => {
        const piece = placeable(el, plugins, enabled);
        return piece ? <Placement key={`${keyOf(el)}:${piece.contract.name}`} el={el} piece={piece} drawings={drawings} held={!drawn.current} /> : null;
      })}
    </PlacedProvider>
  );
}

const keys = new WeakMap<HTMLElement, number>();
let nextKey = 0;
function keyOf(el: HTMLElement): number {
  let key = keys.get(el);
  if (key === undefined) keys.set(el, (key = nextKey++));
  return key;
}

export interface KitPlacementsProps {
  /** Where placeholders are looked for; the page's body by default. */
  readonly scope?: HTMLElement;
  /** The plug-in declarations; every plug-in Telify has by default. */
  readonly plugins?: readonly PluginDeclaration[];
}

/**
 * The page's placeholders, filled inside the providers around it: the kit's
 * own root on a custom page, and on Telify's pages the website's, for the
 * placeholders in a custom site's header (D3). Installs `Telify.draw`.
 */
export function KitPlacements({ scope = document.body, plugins = PLUGINS }: KitPlacementsProps) {
  const [parts] = useState(() => {
    const drawings = new Drawings();
    drawings.install();
    // studio-shop D4: the page's shop, which ties its grid, product window and bag together, starts with nothing open.
    return { placeholders: new Placeholders(scope), drawings, shopPage: new ShopPage() };
  });
  useEffect(() => {
    const observer = new MutationObserver(parts.placeholders.scan);
    observer.observe(scope, { childList: true, subtree: true });
    parts.placeholders.scan();
    return () => observer.disconnect();
  }, [parts, scope]);
  return (
    <ShopPageProvider value={parts.shopPage}>
      <Placements placeholders={parts.placeholders} plugins={plugins} drawings={parts.drawings} />
    </ShopPageProvider>
  );
}

/** The messages panel and banner, once for the page, while a client is signed in; a guest's page never loads their code. */
const SignedInPartsCode = lazy(() => import('./SignedInParts'));
function SignedInParts() {
  return useWebSession().status === 'signedIn' ? (
    <Suspense fallback={null}>
      <SignedInPartsCode />
    </Suspense>
  ) : null;
}

/** Fires `telify:signed-in` and `telify:signed-out` when the visitor signs in or out on this page. */
export function useSessionPageEvents(): void {
  const { status } = useWebSession();
  const last = useRef(status);
  useEffect(() => {
    const prev = last.current;
    last.current = status;
    if (prev === 'signedOut' && status === 'signedIn') dispatchPageEvent('telify:signed-in', null);
    if (prev === 'signedIn' && status === 'signedOut') dispatchPageEvent('telify:signed-out', null);
  }, [status]);
}

function KitPage(props: KitPlacementsProps) {
  // web-sign-in-checkout D5: a signed-in request answering 401 signs the visitor out everywhere on the page.
  const queryClient = useQueryClient();
  useEffect(() => bindSessionEnd(queryClient), [queryClient]);
  useSessionPageEvents();
  return (
    <MessagesPanelProvider>
      <WebChatProvider>
        <ToastProvider>
          <KitPlacements {...props} />
          <SignedInParts />
        </ToastProvider>
      </WebChatProvider>
    </MessagesPanelProvider>
  );
}

interface KitRootProps extends KitPlacementsProps {
  readonly queryClient: QueryClient;
}

function KitRoot({ queryClient, ...props }: KitRootProps) {
  // A router on the document's own address, so the pieces that read it keep working.
  const [router] = useState(() => createBrowserRouter([{ path: '*', element: <KitPage {...props} /> }]));
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

export function startKit({
  scope = document.body,
  plugins = PLUGINS,
  queryClient = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } } }),
}: KitOptions = {}): Kit {
  const host = document.createElement('div');
  host.setAttribute(KIT_HOST_ATTRIBUTE, '');
  document.body.append(host);
  const root = createRoot(host);
  root.render(<KitRoot scope={scope} plugins={plugins} queryClient={queryClient} />);
  return {
    stop: () => {
      root.unmount();
      host.remove();
    },
  };
}
